import { useGlobalMeetingAccess } from '@/hooks/global/useGlobalMeetingPermissions';
import { Navigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useEffect, useState } from 'react';
import { useAdminCheck } from '@/hooks/useAdminCheck';

interface MeetingProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * MeetingProtectedRoute
 * ✅ Route guard for Meetings page
 * ✅ Uses MEETING_ACCESS permission via useGlobalMeetingAccess
 */
const MeetingProtectedRoute = ({ children }: MeetingProtectedRouteProps) => {
  const { isAdmin, isLoading: isAdminLoading } = useAdminCheck();
  const { data: hasAccess, isLoading } = useGlobalMeetingAccess();
  const { toast } = useToast();
  const [timeoutReached, setTimeoutReached] = useState(false);

  // Timeout fallback: redirect after 5 seconds if still loading
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isLoading) {
        console.warn('⚠️ MeetingProtectedRoute: Permission check timeout after 5s');
        setTimeoutReached(true);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [isLoading]);

  // Show toast only once when access is denied (skip for admin)
  useEffect(() => {
    if (!isAdmin && !isLoading && !hasAccess) {
      toast({
        title: "Truy cập bị từ chối",
        description: "Bạn không có quyền truy cập trang Cuộc họp.",
        variant: "destructive",
      });
    }
  }, [isAdmin, hasAccess, isLoading, toast]);

  // Loading state
  if ((isLoading || isAdminLoading) && !timeoutReached) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Đang kiểm tra quyền truy cập...</p>
        </div>
      </div>
    );
  }

  // Redirect if timeout or no access (admin always has access)
  if (timeoutReached || (!isLoading && !isAdmin && !hasAccess)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default MeetingProtectedRoute;
